import type { TutorialSlide } from '../screens/TutorialScreen'
import { NeonColors, FONTS } from '../neon-theme'
import { generateSnakeColors, getPlayerColorKey } from '../../game/snakeColors'
import { HEADING_FONT, SUB_FONT, TITLE_FONT_SIZE } from './tutorialStyles'

const SCALE = 2
const BASE_SPEED = 1000
const TURN_RATE = 4.2
const SEGMENT_SPACING = 60
const INITIAL_WIDTH = 120
const WORLD_SCALE = 0.1

// All on one screen, one camera, no transitions between scenes.
// Timeline:
// 0–4:    portal top-right, green snake enters left wiggles toward portal, "Cash out sparks"
// 4–4.3:  touch flash, snake body white
// 4.3–5:  snake sucks in fast (0.7s)
// 5–7.5:  "+1⭐" floats up with "1 Star for every 100 sparks saved"
// 7.5–8:  portal shrinks to micro-dot
// 8–8.5:  new portal grows from micro-dot, bottom area, slightly left of center
// 8.5–10: "Portal relocates every 60 seconds"
// 10–12:  green snake #2 enters top-right heading toward portal, red snake enters from left above portal blocking path
// 12–12.5: collision, green explodes
// 12.5–15: "Die before evacuation? Sparks stay on the map"
// 15–16.5: fade out
// 16.5–18: fade in
const LOOP_DURATION = 18

interface Seg { x: number; z: number }
interface DeathSpark { x: number; z: number; vx: number; vz: number; alpha: number }

function normalizeRadians(a: number): number {
  let r = a % (Math.PI * 2)
  if (r > Math.PI) r -= Math.PI * 2
  if (r < -Math.PI) r += Math.PI * 2
  return r
}

function hexToStr(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`
}

class Snake {
  headX: number; headZ: number; bearing: number; desiredBearing: number
  segments: Seg[] = []; alive = true
  constructor(x: number, z: number, bearing: number, segCount: number) {
    this.headX = x; this.headZ = z; this.bearing = bearing; this.desiredBearing = bearing
    for (let i = 0; i < segCount; i++) {
      this.segments.push({ x: x - Math.cos(bearing) * i * SEGMENT_SPACING, z: z - Math.sin(bearing) * i * SEGMENT_SPACING })
    }
  }
  step(dt: number, speed = BASE_SPEED): void {
    if (!this.alive) return
    const maxD = TURN_RATE * dt
    const diff = normalizeRadians(this.desiredBearing - this.bearing)
    this.bearing = Math.abs(diff) <= maxD ? normalizeRadians(this.desiredBearing) : normalizeRadians(this.bearing + Math.sign(diff) * maxD)
    this.headX += Math.cos(this.bearing) * speed * dt
    this.headZ += Math.sin(this.bearing) * speed * dt
    this.segments[0] = { x: this.headX, z: this.headZ }
    for (let i = 1; i < this.segments.length; i++) {
      const prev = this.segments[i - 1]!; const cur = this.segments[i]!
      const dx = cur.x - prev.x, dz = cur.z - prev.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist > SEGMENT_SPACING) { const r = SEGMENT_SPACING / dist; cur.x = prev.x + dx * r; cur.z = prev.z + dz * r }
    }
  }
}

export class PortalSlide implements TutorialSlide {
  private container: HTMLDivElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private canvasW = 0; private canvasH = 0
  private animFrame = 0; private startTime = 0
  private lastFrameTime = 0; private mounted = false

  private mainSnake!: Snake
  private victimSnake!: Snake
  private killerSnake!: Snake

  private snakeBody1 = '#88DD66'; private snakeBody2 = '#66BB44'
  private snakeHead = '#AAFFAA'; private snakeGlow = '#88DD66'

  private deathSparks: DeathSpark[] = []
  private deathTriggered = false
  private reachedPortal = false
  private reachTime = 0

  // Portal 1: top-right area. Portal 2: bottom, slightly left of center.
  // All positions in world space. Camera is static, centered on (1000, 500).
  private camX = 1000; private camZ = 800
  private p1x = 2200; private p1z = -200
  private p2x = 600; private p2z = 1800

  mount(el: HTMLDivElement, _onReady: () => void): void {
    this.container = el; el.style.padding = '0'
    const savedSeed = localStorage.getItem('star-snake-color-seed')
    const colorSeed = savedSeed !== null ? parseInt(savedSeed, 10) || 0 : 0
    const colors = generateSnakeColors(getPlayerColorKey(colorSeed))
    this.snakeBody1 = hexToStr(colors.body[0]); this.snakeBody2 = hexToStr(colors.body[1])
    this.snakeHead = hexToStr(colors.head); this.snakeGlow = hexToStr(colors.body[0])
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%;'
    el.appendChild(this.canvas)
    this.mounted = true; this.canvasW = 0; this.canvasH = 0; this.animFrame = 0
    this.startTime = performance.now(); this.lastFrameTime = this.startTime
    this.resetAll(); this.tick()
  }

  unmount(): void {
    this.mounted = false
    if (this.animFrame) { cancelAnimationFrame(this.animFrame); this.animFrame = 0 }
    if (this.container) this.container.innerHTML = ''
    this.container = null; this.canvas = null; this.ctx = null
  }

  private resetAll(): void {
    this.mainSnake = new Snake(-500, 400, 0, 14) // enters from left toward portal 1
    this.victimSnake = new Snake(2800, -600, Math.PI * 0.7, 20) // enters top-right toward portal 2
    this.killerSnake = new Snake(-500, 1000, 0, 36) // enters from left, above portal 2
    this.deathSparks = []; this.deathTriggered = false
    this.reachedPortal = false; this.reachTime = 0
  }

  private resizeCanvas(): void {
    if (!this.canvas) return
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.floor(rect.width) || 320; const h = Math.floor(rect.height) || 500
    if (w === this.canvasW && h === this.canvasH) return
    this.canvasW = w; this.canvasH = h
    this.canvas.width = w * SCALE; this.canvas.height = h * SCALE
    this.ctx = this.canvas.getContext('2d')
  }

  private tick = (): void => {
    if (!this.mounted || !this.canvas) return
    this.resizeCanvas()
    if (!this.ctx || this.canvasW === 0) { this.animFrame = requestAnimationFrame(this.tick); return }
    const now = performance.now()
    const elapsed = (now - this.startTime) / 1000
    const t = elapsed % LOOP_DURATION
    const dt = Math.min(0.1, (now - this.lastFrameTime) / 1000)
    this.lastFrameTime = now
    if (t >= 16.5 || (t < 0.05 && elapsed > 0.5)) { this.resetAll() }
    this.simulate(t, dt)
    this.draw(t)
    this.animFrame = requestAnimationFrame(this.tick)
  }

  private simulate(t: number, dt: number): void {
    // Main snake → portal 1 (0–4)
    if (!this.reachedPortal && this.mainSnake.alive && t < 5) {
      const dx = this.p1x - this.mainSnake.headX, dz = this.p1z - this.mainSnake.headZ
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist < 200) { this.reachedPortal = true; this.reachTime = t }
      else {
        this.mainSnake.desiredBearing = Math.atan2(dz, dx) + Math.sin(t * 2.5) * 0.2
        let r = dt; while (r > 0) { const s = Math.min(r, 1 / 30); this.mainSnake.step(s); r -= s }
      }
    }

    // Death scene snakes (10+)
    if (t >= 10 && !this.deathTriggered) {
      // Victim: top-right heading toward portal 2
      if (this.victimSnake.alive) {
        const dx = this.p2x - this.victimSnake.headX, dz = this.p2z - this.victimSnake.headZ
        this.victimSnake.desiredBearing = Math.atan2(dz, dx) + Math.sin(t * 2.5) * 0.2
        let r = dt; while (r > 0) { const s = Math.min(r, 1 / 30); this.victimSnake.step(s); r -= s }
      }
      // Killer: from left, moves right to block — full speed, slight wiggle
      if (this.killerSnake.alive) {
        this.killerSnake.desiredBearing = 0 + Math.sin(t * 2.5) * 0.15
        let r = dt; while (r > 0) { const s = Math.min(r, 1 / 30); this.killerSnake.step(s); r -= s }
      }
      // Collision check: victim head vs killer body
      if (this.victimSnake.alive && this.killerSnake.alive) {
        for (let i = 1; i < this.killerSnake.segments.length; i++) {
          const seg = this.killerSnake.segments[i]!
          const ddx = this.victimSnake.headX - seg.x, ddz = this.victimSnake.headZ - seg.z
          if (ddx * ddx + ddz * ddz < 80 * 80) {
            this.deathTriggered = true; this.victimSnake.alive = false
            for (const vs of this.victimSnake.segments) {
              for (let j = 0; j < 3; j++) {
                const a = Math.random() * Math.PI * 2; const sp = 40 + Math.random() * 100
                this.deathSparks.push({ x: vs.x + Math.random() * 20 - 10, z: vs.z + Math.random() * 20 - 10, vx: Math.cos(a) * sp, vz: Math.sin(a) * sp, alpha: 0.7 + Math.random() * 0.3 })
              }
            }
            break
          }
        }
      }
    }
    if (this.deathTriggered && this.killerSnake.alive && t < 15) {
      this.killerSnake.desiredBearing = 0 + Math.sin(t * 2) * 0.1
      let r = dt; while (r > 0) { const s = Math.min(r, 1 / 30); this.killerSnake.step(s); r -= s }
    }
    for (const sp of this.deathSparks) {
      sp.x += sp.vx * dt; sp.z += sp.vz * dt; sp.vx *= 0.97; sp.vz *= 0.97
      sp.alpha = Math.max(0, sp.alpha - dt * 0.15)
    }
  }

  private toSX(wx: number): number { return (wx - this.camX) * WORLD_SCALE * SCALE + (this.canvasW * SCALE) / 2 }
  private toSZ(wz: number): number { return (wz - this.camZ) * WORLD_SCALE * SCALE + (this.canvasH * SCALE) * 0.4 }

  private draw(t: number): void {
    const ctx = this.ctx!
    const w = this.canvasW * SCALE, h = this.canvasH * SCALE, s = SCALE
    const portalR = 30 * s
    const textTopCZ = h * 0.48  // "cash out" — center of screen
    const textMidCZ = h * 0.72  // relocation + death text — closer to action

    const fadeOut = t >= 15 && t < 16.5
    const fadeIn = t >= 16.5
    const gf = fadeOut ? Math.max(0, 1 - (t - 15) / 1.5) : fadeIn ? Math.min(1, (t - 16.5) / 1.5) : 1

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = NeonColors.bgDark; ctx.fillRect(0, 0, w, h)

    // Grid
    const gs = 300
    ctx.strokeStyle = 'rgba(255,255,255,0.025)'; ctx.lineWidth = 1
    const gsX = Math.floor((this.camX - this.canvasW / WORLD_SCALE / 2) / gs) * gs
    const gsZ = Math.floor((this.camZ - this.canvasH / WORLD_SCALE / 2) / gs) * gs
    for (let gx = gsX; gx < this.camX + this.canvasW / WORLD_SCALE; gx += gs) {
      const sx = this.toSX(gx); ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, h); ctx.stroke()
    }
    for (let gz = gsZ; gz < this.camZ + this.canvasH / WORLD_SCALE; gz += gs) {
      const sz = this.toSZ(gz); ctx.beginPath(); ctx.moveTo(0, sz); ctx.lineTo(w, sz); ctx.stroke()
    }

    // Edge fades
    const fs = 30 * s
    let g = ctx.createLinearGradient(0, 0, 0, fs); g.addColorStop(0, NeonColors.bgDark); g.addColorStop(1, 'rgba(0,2,10,0)')
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, fs)
    g = ctx.createLinearGradient(0, h - fs, 0, h); g.addColorStop(0, 'rgba(0,2,10,0)'); g.addColorStop(1, NeonColors.bgDark)
    ctx.fillStyle = g; ctx.fillRect(0, h - fs, w, fs)
    g = ctx.createLinearGradient(0, 0, fs, 0); g.addColorStop(0, NeonColors.bgDark); g.addColorStop(1, 'rgba(0,2,10,0)')
    ctx.fillStyle = g; ctx.fillRect(0, 0, fs, h)
    g = ctx.createLinearGradient(w - fs, 0, w, 0); g.addColorStop(0, 'rgba(0,2,10,0)'); g.addColorStop(1, NeonColors.bgDark)
    ctx.fillStyle = g; ctx.fillRect(w - fs, 0, fs, h)

    // Title
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'
    ctx.font = `bold ${TITLE_FONT_SIZE}px ${FONTS.title}`
    ctx.fillStyle = NeonColors.primary; ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 12 * s
    ctx.globalAlpha = 0.95 * gf; ctx.fillText('EVACUATION', w / 2, 53 * s)
    ctx.shadowBlur = 0; ctx.globalAlpha = 1

    // ---- PORTAL 1 (shrinks 7.5–8) ----
    if (t < 8 || t >= 16.5) {
      let pScale = 1
      if (t >= 7.5 && t < 8) pScale = Math.max(0.03, 1 - (t - 7.5) * 2)
      else if (t >= 16.5) pScale = Math.min(1, (t - 16.5) / 1.5)
      this.drawPortal(ctx, this.toSX(this.p1x), this.toSZ(this.p1z), portalR * pScale, t, gf)
    }

    // ---- PORTAL 2 (grows from micro-dot 8–8.5) ----
    if (t >= 8) {
      let pScale = t < 8.5 ? 0.03 + (t - 8) / 0.5 * 0.97 : 1
      if (t >= 15) pScale *= Math.max(0, 1 - (t - 15) / 1.5)
      if (pScale > 0.01) this.drawPortal(ctx, this.toSX(this.p2x), this.toSZ(this.p2z), portalR * pScale, t, gf)
    }

    // ---- MAIN SNAKE (evacuation) ----
    const rt = this.reachTime
    if (this.mainSnake.alive || (this.reachedPortal && t < rt + 1)) {
      const suckStart = rt + 0.3; const suckEnd = rt + 0.7 // fast suck: 0.4s
      const isSucking = this.reachedPortal && t >= suckStart && t < suckEnd
      const isFlash = this.reachedPortal && t >= rt && t < suckStart
      let visCount = this.mainSnake.segments.length
      let alpha = gf
      if (isSucking) {
        const p = (t - suckStart) / (suckEnd - suckStart)
        visCount = Math.max(0, Math.floor(this.mainSnake.segments.length * (1 - p)))
        for (let i = 0; i < this.mainSnake.segments.length; i++) {
          const pull = Math.max(0, p - (this.mainSnake.segments.length - 1 - i) * 0.03)
          const seg = this.mainSnake.segments[i]!
          seg.x += (this.p1x - seg.x) * pull * pull * 0.4
          seg.z += (this.p1z - seg.z) * pull * pull * 0.4
        }
        alpha = gf * (1 - p * 0.7)
      }
      if (visCount > 0 && (!this.reachedPortal || t < suckEnd)) {
        this.drawWorldSnake(ctx, this.mainSnake.segments, visCount, s, alpha,
          isFlash ? '#FFFFFF' : this.snakeBody1, isFlash ? '#EEEEFF' : this.snakeBody2,
          isFlash ? '#FFFFFF' : this.snakeHead, isFlash ? '#FFFFFF' : this.snakeGlow)
      }
      if (!this.reachedPortal) {
        const hx = this.toSX(this.mainSnake.headX), hz = this.toSZ(this.mainSnake.headZ)
        ctx.font = `bold ${Math.round(9 * 1.1 * s)}px ${FONTS.body}`; ctx.fillStyle = NeonColors.gold
        ctx.globalAlpha = 0.8 * gf; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
        ctx.fillText('156', hx + 12 * s, hz + 12 * s); ctx.globalAlpha = 1
      }
    }

    // Touch flash ring
    if (this.reachedPortal && t >= rt && t < rt + 0.3) {
      const fp = (t - rt) / 0.3; const px = this.toSX(this.p1x), pz = this.toSZ(this.p1z)
      ctx.beginPath(); ctx.arc(px, pz, portalR * (1 + fp), 0, Math.PI * 2)
      ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 3 * s
      ctx.globalAlpha = (1 - fp) * 0.5 * gf; ctx.stroke(); ctx.globalAlpha = 1
    }

    // "+1 ⭐" floats up from portal + explanation text left of portal
    if (this.reachedPortal && t >= rt + 0.5 && t < rt + 3.5) {
      const elapsed = t - rt - 0.5; const floatP = Math.min(1, elapsed / 1.2)
      const ep = floatP * floatP * (3 - 2 * floatP)
      const px = this.toSX(this.p1x); const pz = this.toSZ(this.p1z) - 30 * s - ep * 50 * s
      const a = Math.min(1, elapsed / 0.3)
      // "+1 ⭐" centered on portal
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = `bold ${18 * s}px ${FONTS.body}`; ctx.fillStyle = NeonColors.gold
      ctx.shadowColor = NeonColors.goldGlow; ctx.shadowBlur = 12 * s; ctx.globalAlpha = a * gf
      ctx.fillText('+1 ⭐', px, pz); ctx.shadowBlur = 0
      // Explanation — left of portal 1, vertically centered with portal
      if (elapsed > 0.6) {
        const ta = Math.min(1, (elapsed - 0.6) / 0.3)
        const p1ScreenX = this.toSX(this.p1x)
        const p1ScreenZ = this.toSZ(this.p1z)
        const textX = p1ScreenX - portalR * 2.5  // left of portal
        ctx.textAlign = 'right'; ctx.textBaseline = 'middle'
        ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
        ctx.globalAlpha = ta * 0.8 * gf; ctx.fillText('1 Star per 100 sparks saved', textX, p1ScreenZ - 9 * s)
        ctx.font = SUB_FONT; ctx.fillStyle = NeonColors.textMuted
        ctx.globalAlpha = ta * 0.5 * gf; ctx.fillText('The change goes to the game', textX, p1ScreenZ + 7 * s)
      }
      ctx.globalAlpha = 1
    }

    // ---- DEATH SCENE SNAKES ----
    if (t >= 10 && t < 15) {
      // Killer (red, 36 segs) — behind
      if (this.killerSnake.alive) this.drawWorldSnake(ctx, this.killerSnake.segments, this.killerSnake.segments.length, s, gf, '#DD6666', '#BB4444', '#FFAAAA', '#DD6666')
      // Victim (green, 20 segs)
      if (this.victimSnake.alive) {
        this.drawWorldSnake(ctx, this.victimSnake.segments, this.victimSnake.segments.length, s, gf, '#88DD66', '#66BB44', '#AAFFAA', '#88DD66')
        const vhx = this.toSX(this.victimSnake.headX), vhz = this.toSZ(this.victimSnake.headZ)
        ctx.font = `bold ${Math.round(9 * 1.1 * s)}px ${FONTS.body}`; ctx.fillStyle = NeonColors.gold
        ctx.globalAlpha = 0.8 * gf; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
        ctx.fillText('123', vhx + 12 * s, vhz - 12 * s); ctx.globalAlpha = 1
      } else if (this.deathTriggered) {
        const de = t - 12.5
        if (de > 0 && de < 1.5) {
          const vhx = this.toSX(this.victimSnake.headX), vhz = this.toSZ(this.victimSnake.headZ)
          ctx.font = `bold ${Math.round(9 * 1.1 * s)}px ${FONTS.body}`; ctx.fillStyle = NeonColors.gold
          ctx.globalAlpha = Math.max(0, 1 - de / 0.8) * 0.7 * gf; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
          ctx.fillText('123', vhx + 12 * s, vhz - 12 * s - de * 20 * s); ctx.globalAlpha = 1
        }
      }
    }

    // Death sparks
    for (const sp of this.deathSparks) {
      if (sp.alpha <= 0.01) continue
      const sx = this.toSX(sp.x), sz = this.toSZ(sp.z)
      const sparkR = (1.5 + (Math.abs(sp.vx) % 1)) * s
      ctx.beginPath(); ctx.arc(sx, sz, sparkR, 0, Math.PI * 2)
      ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = sp.alpha * 0.8 * gf
      ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = sparkR * 2
      ctx.fill(); ctx.shadowBlur = 0
    }
    ctx.globalAlpha = 1

    // ---- TEXT ----
    const starTextEnd = this.reachedPortal ? this.reachTime + 3.5 : 999
    const p1sx = this.toSX(this.p1x), p1sz = this.toSZ(this.p1z)
    const p2sx = this.toSX(this.p2x), p2sz = this.toSZ(this.p2z)

    // "Cash out your sparks through portals" — centered, single heading line
    if (t < 7.5) {
      const fadeA = this.reachedPortal && t >= this.reachTime + 1 ? Math.max(0, 1 - (t - this.reachTime - 1) / 0.5) : 1
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      ctx.globalAlpha = 0.85 * gf * fadeA
      ctx.fillText('Cash out your sparks through portals', w / 2, textTopCZ)
    }

    // "Portal relocates every 60 seconds" — centered, single heading line
    if (t >= starTextEnd && t < 10) {
      const a = Math.min(1, (t - starTextEnd) / 0.4)
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      ctx.globalAlpha = a * 0.85 * gf
      ctx.fillText('Portal relocates every 60 seconds', w / 2, textMidCZ)
    }

    // "Die before evacuation?" — right of portal 2, vertically centered with it
    if (t >= 12.5 && t < 16) {
      const a = Math.min(1, (t - 12.5) / 0.4)
      const fadeA = t >= 15.5 ? Math.max(0, 1 - (t - 15.5) / 0.5) : 1
      const dtx = p2sx + portalR * 2  // right of portal 2
      const dtz = p2sz  // vertically centered with portal
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.danger
      ctx.globalAlpha = a * 0.9 * fadeA
      ctx.fillText('Die before evacuation?', dtx, dtz - 9 * s)
      ctx.font = SUB_FONT; ctx.fillStyle = NeonColors.textBright
      ctx.globalAlpha = a * 0.75 * fadeA
      ctx.fillText('Your sparks stay on the map', dtx, dtz + 7 * s)
    }
    ctx.globalAlpha = 1

    // Fade overlay
    if (gf < 1) { ctx.fillStyle = NeonColors.bgDark; ctx.globalAlpha = 1 - gf; ctx.fillRect(0, 0, w, h); ctx.globalAlpha = 1 }
  }

  private drawPortal(ctx: CanvasRenderingContext2D, cx: number, cz: number, radius: number, t: number, alpha: number): void {
    if (radius < 1 || alpha < 0.01) return; const s = SCALE; const now = t * 3
    ctx.beginPath(); ctx.arc(cx, cz, radius * 1.4, 0, Math.PI * 2)
    ctx.fillStyle = NeonColors.portal; ctx.globalAlpha = alpha * 0.08; ctx.fill()
    for (let arm = 0; arm < 3; arm++) {
      const off = (arm * Math.PI * 2) / 3; ctx.beginPath()
      for (let i = 0; i <= 20; i++) {
        const f = i / 20; const r = radius * (1 - f * 0.85)
        const a = off + now * 2 + f * Math.PI * 2.5
        const sx = cx + Math.cos(a) * r, sz = cz + Math.sin(a) * r
        if (i === 0) ctx.moveTo(sx, sz); else ctx.lineTo(sx, sz)
      }
      ctx.strokeStyle = NeonColors.portal; ctx.globalAlpha = alpha * 0.6; ctx.lineWidth = 2 * s; ctx.stroke()
    }
    ctx.beginPath(); ctx.arc(cx, cz, radius * 0.15, 0, Math.PI * 2)
    ctx.fillStyle = '#FFFFFF'; ctx.globalAlpha = alpha * 0.7
    ctx.shadowColor = NeonColors.portal; ctx.shadowBlur = 8 * s; ctx.fill(); ctx.shadowBlur = 0; ctx.globalAlpha = 1
  }

  private drawWorldSnake(ctx: CanvasRenderingContext2D, segs: Seg[], count: number, s: number, alpha: number,
    body1: string, body2: string, head: string, glow: string): void {
    if (count < 1) return
    const bodyR = INITIAL_WIDTH * WORLD_SCALE * s * 0.5; const headR = bodyR * 1.3
    for (let i = Math.min(count - 1, 20); i >= 0; i--) {
      const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR * 1.5, 0, Math.PI * 2)
      ctx.fillStyle = glow; ctx.globalAlpha = 0.045 * alpha; ctx.fill()
    }
    for (let i = count - 1; i >= 1; i--) {
      const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR, 0, Math.PI * 2)
      ctx.fillStyle = i % 2 === 0 ? body1 : body2
      ctx.globalAlpha = (i < 3 ? 0.5 + i * 0.15 : 0.94) * alpha; ctx.fill()
    }
    ctx.globalAlpha = 1
    const hx = this.toSX(segs[0]!.x), hz = this.toSZ(segs[0]!.z)
    ctx.beginPath(); ctx.arc(hx, hz, headR, 0, Math.PI * 2)
    ctx.fillStyle = head; ctx.shadowColor = glow; ctx.shadowBlur = headR
    ctx.globalAlpha = alpha; ctx.fill(); ctx.shadowBlur = 0
    if (count >= 2) {
      const dx = segs[0]!.x - segs[1]!.x, dz = segs[0]!.z - segs[1]!.z
      const bearing = Math.atan2(dz, dx); const eco = Math.cos(bearing), esi = Math.sin(bearing)
      const off = headR * 0.55, r = headR * 0.28
      for (const side of [-1, 1]) {
        const ex = hx + eco * off * 0.4 - esi * off * side, ez = hz + esi * off * 0.4 + eco * off * side
        ctx.beginPath(); ctx.arc(ex, ez, r, 0, Math.PI * 2); ctx.fillStyle = '#FFFFFF'; ctx.globalAlpha = alpha; ctx.fill()
        ctx.beginPath(); ctx.arc(ex + eco * r * 0.4, ez + esi * r * 0.4, r * 0.4, 0, Math.PI * 2); ctx.fillStyle = '#000000'; ctx.fill()
      }
    }
    ctx.globalAlpha = 1
  }
}
